import { MutationCache, QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { hasRecentToast } from './notify';

// Stable sonner ids make repeated generic toasts replace each other instead of
// stacking. This is the safety net that keeps a burst of mutations (for example
// a bulk action that loops an endpoint) showing a single toast.
const GENERIC_SUCCESS_TOAST_ID = 'fa-global-mutation-success';
const GENERIC_ERROR_TOAST_ID = 'fa-global-mutation-error';

/**
 * The application's single query cache.
 *
 * Exported so identity changes can drop every cached response: query keys are
 * not user-scoped, so without an explicit `clear()` the next login in the same
 * tab could render the previous user's data until `staleTime` expires (the
 * backend's Redis cache is per user, but the client never asks it).
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
  mutationCache: new MutationCache({
    onSuccess: (_data, _variables, _context, mutation) => {
      const meta = mutation.meta as { successMessage?: string; hideToast?: boolean } | undefined
      if (meta?.hideToast) return
      const message = meta?.successMessage || 'Saved successfully'
      // Defer one macrotask: if the calling page already shows its own toast
      // for this mutation (of any kind, including a partial-success warning),
      // skip this generic fallback instead of stacking two.
      setTimeout(() => {
        if (hasRecentToast()) return
        toast.success(message, { id: GENERIC_SUCCESS_TOAST_ID })
      }, 0)
    },
    onError: (error: any, _variables, _context, mutation) => {
      const meta = mutation.meta as { errorMessage?: string; hideToast?: boolean } | undefined
      if (meta?.hideToast) return
      const message = error?.response?.data?.message || error?.message || 'Something went wrong'
      const finalMessage = meta?.errorMessage || message
      setTimeout(() => {
        if (hasRecentToast()) return
        toast.error(finalMessage, { id: GENERIC_ERROR_TOAST_ID })
      }, 0)
    },
  }),
});
